import { NavigationRoutes } from "@Navigation/NavigationRoutes";
import { Form } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useResendOtp, useVerifyUserOtp } from "./OtpQuery.tsx";
import { OtpResetPayload, ResendOtpPayload } from "../Login/types";

export default function useOtpContainer() {
  const [otp, setOTP] = useState<string[]>(new Array(6).fill(""));
  const location = useLocation();
  const navigate = useNavigate();

  let { email } = location.state;

  const [form] = Form.useForm();

  const RESEND_OTP_INTERVAL_SECONDS = 60;
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  const canResend = secondsLeft === 0;

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [secondsLeft]);

  const formatSecondsToMMSS = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const onVerifyUserOtpSuccess = (data: any, payload: OtpResetPayload) => {
    const tokenFromApi = data?.data as string | undefined;
    const emailFromPayload = payload?.email ?? null;
    // Fallback: if API does not return token, use the entered OTP as token
    const tokenToUse =
      tokenFromApi && tokenFromApi.length > 0
        ? tokenFromApi
        : payload?.otp ?? "";

    if (tokenToUse) sessionStorage.setItem("resetPasswordToken", tokenToUse);
    if (emailFromPayload)
      sessionStorage.setItem("resetPasswordEmail", emailFromPayload);

    navigate(NavigationRoutes.AUTH_ROUTES.RESET_PASSWORD, {
      state: { token: tokenToUse, email: emailFromPayload },
    });
  };

  const { mutate: verifyUserOtp, isPending: isVerifyingUser } =
    useVerifyUserOtp(onVerifyUserOtpSuccess);

  const { mutate: resendOtp, isPending: isResendingOtp } = useResendOtp(
    () => {}
  );

  const handleSubmit = () => {
    const code = otp.join("");
    if (code.length !== 6) {
      return;
    }
    const payload: OtpResetPayload = { otp: code, email };
    verifyUserOtp(payload);
  };

  const handleResendOtp = () => {
    if (!email || isResendingOtp || secondsLeft > 0) return;
    setSecondsLeft(RESEND_OTP_INTERVAL_SECONDS);
    const payload: ResendOtpPayload = { email };
    resendOtp(payload);
  };

  return {
    otp,
    email,
    setOTP,
    form,
    navigate,
    handleSubmit,
    handleResendOtp,
    isResendingOtp,
    isVerifyingUser,
    canResend,
    secondsLeft,

    timeLeftFormatted: formatSecondsToMMSS(
      secondsLeft === 0 ? RESEND_OTP_INTERVAL_SECONDS : secondsLeft
    ),
  };
}
